// PlacePicker -- debounced Google Places autocomplete via our backend proxy.
// The API key never reaches the browser; all Google calls go through /api/places/*.
//
// Setup (server): set GOOGLE_MAPS_API_KEY in packages/api/.env
// Enable "Places API (New)" at console.cloud.google.com. Billing must be active.
//
// Session tokens are generated on mount and rotated after each completed selection
// (autocomplete + details = one billable session rather than per-keystroke charges).

import { useCallback, useEffect, useRef, useState } from "react";
import { ExternalLink, Loader2, MapPin, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import * as api from "@/lib/api";
import type { PlaceSuggestion } from "@/lib/api";

export interface SelectedPlace {
  id: string;
  name: string;
  address: string;
  lat: number;
  lng: number;
}

interface PlacePickerProps {
  onSelect: (place: SelectedPlace) => void;
  label?: string;
  placeholder?: string;
}

export function PlacePicker({
  onSelect,
  label = "Search Google Places",
  placeholder = "Type a venue or school name…",
}: PlacePickerProps) {
  const [query, setQuery] = useState("");
  const [suggestions, setSuggestions] = useState<PlaceSuggestion[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [fetchingDetails, setFetchingDetails] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [selected, setSelected] = useState<SelectedPlace | null>(null);

  const sessionTokenRef = useRef<string>(crypto.randomUUID());
  const debounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputId = useRef(`place-picker-${crypto.randomUUID().slice(0, 8)}`);

  // Close on click outside
  useEffect(() => {
    function onMouseDown(e: MouseEvent) {
      if (!containerRef.current?.contains(e.target as Node)) setIsOpen(false);
    }
    document.addEventListener("mousedown", onMouseDown);
    return () => document.removeEventListener("mousedown", onMouseDown);
  }, []);

  const search = useCallback(async (value: string) => {
    if (value.length < 2) {
      setSuggestions([]);
      setIsOpen(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await api.getPlacesAutocomplete(value, sessionTokenRef.current);
      setSuggestions(res.suggestions);
      setIsOpen(true);
      setActiveIndex(-1);
    } catch {
      setError("Search failed -- check your connection");
      setSuggestions([]);
      setIsOpen(false);
    } finally {
      setLoading(false);
    }
  }, []);

  function handleChange(value: string) {
    setQuery(value);
    setSelected(null);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => search(value), 250);
  }

  async function handleSelect(suggestion: PlaceSuggestion) {
    setIsOpen(false);
    setQuery(suggestion.text);
    setSuggestions([]);
    setFetchingDetails(true);
    setError(null);
    try {
      const place = await api.getPlaceDetails(suggestion.placeId, sessionTokenRef.current);
      const selected: SelectedPlace = {
        id: place.id,
        name: place.name,
        address: place.address,
        lat: place.lat,
        lng: place.lng,
      };
      setSelected(selected);
      onSelect(selected);
      // Rotate session token -- this completes one billable session
      sessionTokenRef.current = crypto.randomUUID();
    } catch {
      setError("Failed to load place details");
    } finally {
      setFetchingDetails(false);
    }
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (!isOpen || suggestions.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, suggestions.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter" && activeIndex >= 0) {
      e.preventDefault();
      handleSelect(suggestions[activeIndex]);
    } else if (e.key === "Escape") {
      setIsOpen(false);
    }
  }

  function clear() {
    setQuery("");
    setSelected(null);
    setSuggestions([]);
    setIsOpen(false);
    setError(null);
  }

  const mapsUrl = selected
    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(selected.name)}&query_place_id=${selected.id}`
    : null;

  const listboxId = `${inputId.current}-listbox`;
  const isSpinning = loading || fetchingDetails;

  return (
    <div ref={containerRef} className="flex flex-col gap-1.5">
      <Label htmlFor={inputId.current}>{label}</Label>
      <div className="relative">
        <MapPin className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          id={inputId.current}
          role="combobox"
          aria-expanded={isOpen}
          aria-haspopup="listbox"
          aria-controls={listboxId}
          aria-activedescendant={activeIndex >= 0 ? `${listboxId}-opt-${activeIndex}` : undefined}
          aria-autocomplete="list"
          autoComplete="off"
          value={query}
          onChange={(e) => handleChange(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => { if (suggestions.length > 0) setIsOpen(true); }}
          placeholder={placeholder}
          className="pl-9 pr-9"
        />
        <div className="absolute right-3 top-1/2 -translate-y-1/2">
          {isSpinning ? (
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          ) : query ? (
            <button
              type="button"
              aria-label="Clear"
              onClick={clear}
              className="rounded text-muted-foreground hover:text-foreground transition-colors"
            >
              <X className="h-4 w-4" />
            </button>
          ) : null}
        </div>

        {isOpen && (
          <div
            id={listboxId}
            role="listbox"
            aria-label="Place suggestions"
            className="absolute top-full left-0 right-0 z-50 mt-1 max-h-60 overflow-y-auto rounded-md border border-border bg-background shadow-lg"
          >
            {suggestions.length === 0 ? (
              <div className="px-3 py-2 text-sm text-muted-foreground">No results</div>
            ) : (
              suggestions.map((s, i) => (
                <div
                  key={s.placeId}
                  id={`${listboxId}-opt-${i}`}
                  role="option"
                  aria-selected={i === activeIndex}
                  onMouseDown={(e) => { e.preventDefault(); handleSelect(s); }}
                  onMouseEnter={() => setActiveIndex(i)}
                  className={`cursor-pointer px-3 py-2 text-sm transition-colors ${
                    i === activeIndex ? "bg-accent text-accent-foreground" : "hover:bg-muted"
                  }`}
                >
                  <div className="font-medium">{s.mainText ?? s.text}</div>
                  {s.secondaryText && (
                    <div className="text-xs text-muted-foreground">{s.secondaryText}</div>
                  )}
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {error && <p className="text-xs text-destructive">{error}</p>}

      {selected && mapsUrl && (
        <a
          href={mapsUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex w-fit items-center gap-1.5 text-xs text-primary hover:underline"
        >
          <ExternalLink className="h-3.5 w-3.5" />
          View on Google Maps
        </a>
      )}
    </div>
  );
}
